import { useMemo } from 'react'

import { matchFilterKey, matchFilterNumber, sortArrayBy } from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { EntityOptions } from '@redux/modules/module'
import type { ActivityModel } from '@models/types'

const watchEntityKeys = ['activities']

type ActivityFilters = {
  key?: string,
  owner_id?: number,
  owner_type?: string,
  recipient_id?: number,
  recipient_type?: string,
  trackable_id?: number,
  trackable_type?: string,
}

type UseActivitiesOptions = {
  filters?: ActivityFilters,
  performHttpRequests?: boolean,
  requestOptions?: EntityOptions,
}

function useActivities(options: UseActivitiesOptions) {
  const { filters = {}, requestOptions } = options
  const {
    key: filterKey,
    owner_id: filterOwnerId,
    owner_type: filterOwnerType,
    recipient_id: filterRecipientId,
    recipient_type: filterRecipientType,
    trackable_id: filterTrackableId,
    trackable_type: filterTrackableType,
  } = filters

  const {
    updatedEntities: { activities: activitiesUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { activities } = useSelector(reduxState => reduxState.entities)

  // Filter redux entities with provided filters
  const filteredActivities = useMemo(() => {
    const filtered = Object.values(activities).filter((activity: ActivityModel) => {
      const {
        key,
        recipient_id,
        recipient_type,
        owner_id,
        owner_type,
        trackable_id,
        trackable_type,
      } = activity

      const matchKey = matchFilterKey(key, filterKey)
      const matchOwnerId = matchFilterNumber(owner_id, filterOwnerId)
      const matchOwnerType = matchFilterKey(owner_type, filterOwnerType)
      const matchRecipientId = matchFilterNumber(recipient_id, filterRecipientId)
      const matchRecipientType = matchFilterKey(recipient_type, filterRecipientType)
      const matchTrackableId = matchFilterNumber(trackable_id, filterTrackableId)
      const matchTrackableType = matchFilterKey(trackable_type, filterTrackableType)

      return matchKey
        && matchOwnerId
        && matchOwnerType
        && (
          (filterRecipientId || filterRecipientType)
            ? (matchRecipientId && matchRecipientType)
            : (matchTrackableId && matchTrackableType)
        )
    })

    return sortArrayBy(filtered, 'desc', 'id')
  }, [activitiesUpdatedAt, JSON.stringify(filters)])

  const filteredActivitiesCount = filteredActivities.length
  const hasFilteredActivities = !!filteredActivitiesCount

  // To be used in conjunction with DashboardModule.LoadMoreFooter
  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredActivitiesCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  // Load entities when performHttpRequests is true
  const { loading: loadingActivities } = useReduxAction(
    'activities',
    'loadActivities',
    {
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: ({ loading }) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredActivities,
    filteredActivitiesCount,
    hasFilteredActivities,
    loading: loadingActivities,
  }
}

export default useActivities
